import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { CalendarClock, Search, Loader2, Info, Download, FileUp, FileDown, AlertTriangle, Plus, Trash2, Filter } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { useToast } from "../hooks/use-toast";
import { getCp204Entries, createCp204Entry, updateCp204Entry, deleteCp204Entry, downloadCp204Template, importCp204, exportCp204 } from "../lib/api";
import { cp204Auto, fyeLabel } from "../lib/cp204";

const emptyAdd = { name: "", regNo: "", personInCharge: "", picEmail: "", fye: "" };
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// Financial year end is always a month-end. Reference year is non-leap and irrelevant to the CP204
// schedule (which uses month/day only). Each option shows the last day of the month, no year.
const FYE_REF_YEAR = 2025;
const monthEndDay = (m) => new Date(FYE_REF_YEAR, m, 0).getDate(); // m = 1..12
const monthEndLabel = (m) => `${monthEndDay(m)} ${MONTHS[m - 1]}`;
const MONTH_OPTS = Array.from({ length: 12 }, (_, i) => ({ m: i + 1, label: monthEndLabel(i + 1) }));
const fyeToMonth = (fye) => (fye ? String(Number(String(fye).slice(5, 7))) : "");
const monthToFye = (m) => `${FYE_REF_YEAR}-${String(m).padStart(2, "0")}-${String(monthEndDay(m)).padStart(2, "0")}`;
const fyeMonthEndLabel = (fye) => (fye ? monthEndLabel(Number(String(fye).slice(5, 7))) : "");
// Distinct, sorted by month where the value ends in a month abbreviation (e.g. "31 Dec").
const uniqSorted = (vals) => {
  const mi = (x) => MONTHS.indexOf(String(x).split(" ").pop());
  return [...new Set(vals.filter(Boolean))].sort((a, b) => {
    const ma = mi(a), mb = mi(b);
    if (ma >= 0 && mb >= 0) return ma - mb;
    return String(a).localeCompare(String(b));
  });
};

const Cp204Compliance = () => {
  const { toast } = useToast();
  const fileRef = useRef(null);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [dueFilter, setDueFilter] = useState("all");
  const [fPic, setFPic] = useState("all");
  const [fFye, setFFye] = useState("all");
  const [fCp204, setFCp204] = useState("all");
  const [fRev1, setFRev1] = useState("all");
  const [fRev2, setFRev2] = useState("all");
  const [fRev3, setFRev3] = useState("all");
  const [savingId, setSavingId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(emptyAdd);
  const [adding, setAdding] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setList(await getCp204Entries() || []);
    } catch (e) {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const setField = (id, field, value) =>
    setList((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)));

  const saveField = async (id, patch) => {
    setSavingId(id);
    try {
      await updateCp204Entry(id, patch);
    } catch (e) {
      toast({ title: e?.response?.data?.detail || "Could not save.", variant: "destructive" });
      reload();
    } finally {
      setSavingId(null);
    }
  };

  // Attach schedule, apply search + filters, then sort: due-soon first, then by name.
  const scheduled = useMemo(() => list.map((c) => ({ c, s: cp204Auto(c.fye) })), [list]);

  const opts = useMemo(() => ({
    pic: uniqSorted(scheduled.map(({ c }) => c.personInCharge)),
    fye: uniqSorted(scheduled.map(({ c }) => fyeMonthEndLabel(c.fye))),
    cp204: uniqSorted(scheduled.map(({ s }) => (s ? s.original : ""))),
    rev1: uniqSorted(scheduled.map(({ s }) => (s ? s.rev1 : ""))),
    rev2: uniqSorted(scheduled.map(({ s }) => (s ? s.rev2 : ""))),
    rev3: uniqSorted(scheduled.map(({ s }) => (s ? s.rev3 : ""))),
  }), [scheduled]);

  const rows = useMemo(() => {
    const q = query.toLowerCase();
    const dueWindow = dueFilter === "all" ? null : Number(dueFilter);
    const withSched = scheduled
      .filter(({ c }) =>
        (c.name || "").toLowerCase().includes(q) || (c.regNo || "").toLowerCase().includes(q))
      .filter(({ c, s }) => {
        if (dueWindow && !(s && s.daysToNext <= dueWindow)) return false;
        if (fPic !== "all" && (c.personInCharge || "") !== fPic) return false;
        if (fFye !== "all" && fyeMonthEndLabel(c.fye) !== fFye) return false;
        if (fCp204 !== "all" && (s ? s.original : "") !== fCp204) return false;
        if (fRev1 !== "all" && (s ? s.rev1 : "") !== fRev1) return false;
        if (fRev2 !== "all" && (s ? s.rev2 : "") !== fRev2) return false;
        if (fRev3 !== "all" && (s ? s.rev3 : "") !== fRev3) return false;
        return true;
      });
    withSched.sort((a, b) => {
      const ad = a.s && a.s.dueSoon, bd = b.s && b.s.dueSoon;
      if (ad && bd) return a.s.daysToNext - b.s.daysToNext;
      if (ad) return -1;
      if (bd) return 1;
      return (a.c.name || "").localeCompare(b.c.name || "");
    });
    return withSched;
  }, [scheduled, query, dueFilter, fPic, fFye, fCp204, fRev1, fRev2, fRev3]);

  const dueSoonCount = useMemo(
    () => list.filter((c) => { const s = cp204Auto(c.fye); return s && s.dueSoon; }).length,
    [list]
  );

  const handleTemplate = async () => {
    try { await downloadCp204Template(); }
    catch (e) { toast({ title: "Could not download template.", variant: "destructive" }); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const res = await importCp204(file);
      toast({ title: "Listing imported", description: `${res.created} added${res.skipped ? `, ${res.skipped} already in list (kept)` : ""}.` });
      reload();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || "Import failed.", variant: "destructive" });
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const buildRowsPayload = () => rows.map(({ c, s }) => ({
    name: c.name, regNo: c.regNo, personInCharge: c.personInCharge || "", picEmail: c.picEmail || "",
    fye: fyeMonthEndLabel(c.fye),
    nextDeadline: s ? `${s.nextLabel} — ${s.nextDate}` : "",
    original: s ? s.original : "", rev1: s ? s.rev1 : "", rev2: s ? s.rev2 : "", rev3: s ? s.rev3 : "",
  }));

  const handleExport = async () => {
    if (!rows.length) { toast({ title: "Nothing to export", variant: "destructive" }); return; }
    setExporting(true);
    try {
      await exportCp204(buildRowsPayload());
      toast({ title: "CP204 listing exported" });
    } catch (e) {
      toast({ title: "Could not export.", variant: "destructive" });
    } finally {
      setExporting(false);
    }
  };

  const handleAdd = async () => {
    if (!addForm.name.trim()) { toast({ title: "Please enter a company name.", variant: "destructive" }); return; }
    setAdding(true);
    try {
      await createCp204Entry({
        name: addForm.name.trim(), regNo: addForm.regNo.trim(),
        personInCharge: addForm.personInCharge.trim(), picEmail: addForm.picEmail.trim(), fye: addForm.fye || "",
      });
      toast({ title: "Added", description: addForm.name.trim() });
      setAddOpen(false); setAddForm(emptyAdd);
      reload();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || "Could not add.", variant: "destructive" });
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await deleteCp204Entry(toDelete.id);
      toast({ title: "Removed", description: toDelete.name });
      setToDelete(null);
      reload();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || "Could not remove.", variant: "destructive" });
    } finally {
      setDeleting(false);
    }
  };

  const colFilter = (value, onChange, options, key) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-7 text-xs w-full min-w-[92px] bg-white" data-testid={`cp204-colfilter-${key}`}><SelectValue placeholder="All" /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">All</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div className="max-w-[1500px] mx-auto" data-testid="cp204-page">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded bg-[#1877c9] text-white flex items-center justify-center">
          <CalendarClock size={20} />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-gray-800">CP204 Compliance</h1>
          <p className="text-sm text-gray-500">A standalone CP204 register — add your own entries; deadlines are worked out from each financial year end</p>
        </div>
      </div>

      <div className="flex items-start gap-2 mb-4 rounded-sm border border-[#d9e6f5] bg-[#f3f8fd] px-4 py-3 text-sm text-[#10416b]">
        <Info size={16} className="mt-0.5 shrink-0" />
        <span>
          <strong>CP204</strong> original estimate is due at least 30 days before the basis period begins.
          <strong> CP204A</strong> revisions fall in the <strong>6th</strong>, <strong>9th</strong> and <strong>11th</strong> month of the basis period.
          Dates are based on each entry's financial year end (day &amp; month). Rows with a deadline within 30 days are flagged and pulled to the top.
        </span>
      </div>

      {dueSoonCount > 0 && (
        <div className="flex items-center gap-2 mb-4 rounded-sm border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800" data-testid="cp204-duesoon-banner">
          <AlertTriangle size={16} className="shrink-0" />
          <span><strong>{dueSoonCount}</strong> {dueSoonCount === 1 ? "entry" : "entries"} with a CP204 deadline due within 30 days.</span>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-sm shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 p-4 border-b border-gray-100">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full lg:w-auto">
            <div className="relative w-full sm:w-64">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search company or reg. no..." className="pl-9" data-testid="cp204-search" />
            </div>
            <div className="flex items-center gap-2">
              <Filter size={15} className="text-gray-400 shrink-0" />
              <Select value={dueFilter} onValueChange={setDueFilter}>
                <SelectTrigger className="w-40 h-9" data-testid="cp204-filter-due"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All deadlines</SelectItem>
                  <SelectItem value="30">Due within 30 days</SelectItem>
                  <SelectItem value="60">Due within 60 days</SelectItem>
                  <SelectItem value="90">Due within 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden" onChange={handleImport} data-testid="cp204-import-input" />
            <Button variant="outline" size="sm" onClick={handleTemplate} className="gap-1.5 h-9 border-[#1877c9] text-[#1877c9] hover:bg-[#eef4fb]" data-testid="cp204-template-btn">
              <Download size={15} /> Template
            </Button>
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={importing} className="gap-1.5 h-9 border-[#1877c9] text-[#1877c9] hover:bg-[#eef4fb]" data-testid="cp204-import-btn">
              {importing ? <Loader2 size={15} className="animate-spin" /> : <FileUp size={15} />} Import
            </Button>
            <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting} className="gap-1.5 h-9 border-[#1877c9] text-[#1877c9] hover:bg-[#eef4fb]" data-testid="cp204-export-btn">
              {exporting ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />} Download Excel
            </Button>
            <Button size="sm" onClick={() => { setAddForm(emptyAdd); setAddOpen(true); }} className="gap-1.5 h-9 bg-[#1877c9] hover:bg-[#0f5ea3]" data-testid="cp204-add-btn">
              <Plus size={15} /> Add
            </Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1200px]">
            <thead>
              <tr className="bg-[#f7f9fc] text-gray-600 text-left">
                <th className="px-4 py-3 font-medium">Company Name</th>
                <th className="px-4 py-3 font-medium">Registration No.</th>
                <th className="px-4 py-3 font-medium">Person in Charge</th>
                <th className="px-4 py-3 font-medium">Financial Year End</th>
                <th className="px-4 py-3 font-medium">Next Deadline</th>
                <th className="px-4 py-3 font-medium">CP204</th>
                <th className="px-4 py-3 font-medium">CP204 (1st Revised)</th>
                <th className="px-4 py-3 font-medium">CP204 (2nd Revised)</th>
                <th className="px-4 py-3 font-medium">CP204 (3rd Revised)</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
              <tr className="bg-[#f7f9fc] border-t border-gray-100">
                <th className="px-2 pb-2"></th>
                <th className="px-2 pb-2"></th>
                <th className="px-2 pb-2">{colFilter(fPic, setFPic, opts.pic, "pic")}</th>
                <th className="px-2 pb-2">{colFilter(fFye, setFFye, opts.fye, "fye")}</th>
                <th className="px-2 pb-2"></th>
                <th className="px-2 pb-2">{colFilter(fCp204, setFCp204, opts.cp204, "cp204")}</th>
                <th className="px-2 pb-2">{colFilter(fRev1, setFRev1, opts.rev1, "rev1")}</th>
                <th className="px-2 pb-2">{colFilter(fRev2, setFRev2, opts.rev2, "rev2")}</th>
                <th className="px-2 pb-2">{colFilter(fRev3, setFRev3, opts.rev3, "rev3")}</th>
                <th className="px-2 pb-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ c, s }) => (
                <tr key={c.id} className={`border-t border-gray-100 align-top ${s && s.dueSoon ? "bg-amber-50 hover:bg-amber-100/60" : "hover:bg-[#f9fbfe]"}`} data-testid={`cp204-row-${c.id}`}>
                  <td className="px-4 py-2">
                    <Input value={c.name || ""} onChange={(e) => setField(c.id, "name", e.target.value)} onBlur={(e) => saveField(c.id, { name: e.target.value })} placeholder="Company name" className="h-8 text-sm min-w-[180px] font-medium" data-testid={`cp204-name-${c.id}`} />
                  </td>
                  <td className="px-4 py-2">
                    <Input value={c.regNo || ""} onChange={(e) => setField(c.id, "regNo", e.target.value)} onBlur={(e) => saveField(c.id, { regNo: e.target.value })} placeholder="Reg. no." className="h-8 text-sm w-36" data-testid={`cp204-reg-${c.id}`} />
                  </td>
                  <td className="px-4 py-2">
                    <Input value={c.personInCharge || ""} onChange={(e) => setField(c.id, "personInCharge", e.target.value)} onBlur={(e) => saveField(c.id, { personInCharge: e.target.value })} placeholder="Name" className="h-8 text-sm" data-testid={`cp204-pic-${c.id}`} />
                  </td>
                  <td className="px-4 py-2">
                    <Select value={fyeToMonth(c.fye)} onValueChange={(v) => { const fye = monthToFye(Number(v)); setField(c.id, "fye", fye); saveField(c.id, { fye }); }}>
                      <SelectTrigger className="h-8 text-sm w-32" data-testid={`cp204-fye-${c.id}`}><SelectValue placeholder="Year end" /></SelectTrigger>
                      <SelectContent>
                        {MONTH_OPTS.map((o) => <SelectItem key={o.m} value={String(o.m)}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap" data-testid={`cp204-next-${c.id}`}>
                    {s ? (
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${s.dueSoon ? "bg-amber-200 text-amber-900" : "bg-gray-100 text-gray-600"}`}>
                        {s.nextLabel} · {s.nextDate}{s.dueSoon ? ` (${s.daysToNext}d)` : ""}
                      </span>
                    ) : <span className="text-gray-400">Set year end</span>}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-800" data-testid={`cp204-original-${c.id}`}>{s ? s.original : "—"}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-700" data-testid={`cp204-rev1-${c.id}`}>{s ? s.rev1 : "—"}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-700" data-testid={`cp204-rev2-${c.id}`}>{s ? s.rev2 : "—"}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-700" data-testid={`cp204-rev3-${c.id}`}>{s ? s.rev3 : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setToDelete(c)} title="Remove entry" className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded" data-testid={`cp204-remove-${c.id}`}>
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-10 text-center text-gray-400">
                    {loading ? (<><Loader2 className="inline animate-spin mr-2" size={16} />Loading…</>) : "No entries yet. Use Add or Import to build your CP204 register."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {savingId && (
          <div className="px-4 py-2 text-xs text-gray-400 flex items-center gap-1.5 border-t border-gray-100">
            <Loader2 className="animate-spin" size={12} /> Saving…
          </div>
        )}
      </div>

      <Dialog open={addOpen} onOpenChange={(o) => { if (!o) { setAddOpen(false); setAddForm(emptyAdd); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add CP204 entry</DialogTitle>
            <DialogDescription>This adds a standalone CP204 compliance entry. It does not create a tax-computation company.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <Label className="text-sm">Company Name</Label>
              <Input value={addForm.name} onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))} placeholder="ABC SDN. BHD." data-testid="cp204-add-name" />
            </div>
            <div>
              <Label className="text-sm">Registration Number</Label>
              <Input value={addForm.regNo} onChange={(e) => setAddForm((f) => ({ ...f, regNo: e.target.value }))} placeholder="202201000001" data-testid="cp204-add-reg" />
            </div>
            <div>
              <Label className="text-sm">Person in Charge</Label>
              <Input value={addForm.personInCharge} onChange={(e) => setAddForm((f) => ({ ...f, personInCharge: e.target.value }))} placeholder="Optional" data-testid="cp204-add-pic" />
            </div>
            <div>
              <Label className="text-sm">Financial Year End</Label>
              <Select value={fyeToMonth(addForm.fye)} onValueChange={(v) => setAddForm((f) => ({ ...f, fye: monthToFye(Number(v)) }))}>
                <SelectTrigger data-testid="cp204-add-fye"><SelectValue placeholder="Select month end" /></SelectTrigger>
                <SelectContent>
                  {MONTH_OPTS.map((o) => <SelectItem key={o.m} value={String(o.m)}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAddOpen(false); setAddForm(emptyAdd); }}>Cancel</Button>
            <Button onClick={handleAdd} disabled={adding} className="bg-[#1877c9] hover:bg-[#0f5ea3]" data-testid="cp204-add-save">
              {adding ? "Adding…" : "Add entry"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove entry?</AlertDialogTitle>
            <AlertDialogDescription>
              {toDelete ? `This removes "${toDelete.name}" from the CP204 register. It does not affect any company tax computation.` : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700 disabled:opacity-40" data-testid="cp204-del-confirm">
              {deleting ? "Removing…" : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Cp204Compliance;
